import { useState, useEffect, useRef } from "react";
import { AIFlowchartContent } from "../../types/ai-templates";
import {
  CheckCircleIcon,
  InformationCircleIcon,
  UserIcon,
  StarIcon,
  ChartBarIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";

export function FlowchartTemplate6({
  content,
  onUpdate,
}: {
  content: AIFlowchartContent;
  onUpdate: (newContent: AIFlowchartContent) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [aspectRatio] = useState<number>(864 / 1104); // SVG aspect ratio

  // Maintain aspect ratio on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = width / aspectRatio;
        containerRef.current.style.height = `${height}px`;
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => window.removeEventListener("resize", updateDimensions);
  }, [aspectRatio]);

  // Define colors
  const colors = {
    background: "#ffffff",
    blue: "#1ac3fb",
    green: "#43dd93",
    lightGreen: "#a6da37",
    yellow: "#ffe711",
    orange: "#f79438",
    red: "#fb6762",
    textHeading: "#171717",
    textDescription: "#4f91fc", // Adjusted to match SVG
  };

  // Map step indices to colors
  const getStepColor = (index: number) => {
    switch (index % 6) {
      case 0:
        return colors.blue;
      case 1:
        return colors.green;
      case 2:
        return colors.lightGreen;
      case 3:
        return colors.yellow;
      case 4:
        return colors.orange;
      case 5:
        return colors.red;
      default:
        return colors.blue;
    }
  };

  // Map step indices to icons
  const getStepIcon = (index: number) => {
    const icons = [
      CheckCircleIcon,
      InformationCircleIcon,
      UserIcon,
      StarIcon,
      ChartBarIcon,
      RocketLaunchIcon,
    ];
    const IconComponent = icons[index % icons.length];
    return (
      <IconComponent
        className="w-8 h-8"
        style={{ color: getStepColor(index) }}
      />
    );
  };

  // Get quadrant title
  const getQuadrantTitle = (index: number, step: any): string => {
    const defaultTitles = [
      "Valuable Information",
      "Interest Demonstration",
      "Engagement Display",
      "Company Culture Insights",
      "Team Dynamics Exploration",
      "Growth Opportunities Discovery",
    ];
    return step.title || defaultTitles[index % 6] || `Step ${index + 1}`;
  };

  // Handle content update
  const handleContentUpdate = (
    index: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index][field] = newText;
      onUpdate(newContent);
    }
  };

  // Render title
  const renderTitle = () => {
    const title =
      content.title || "Unveiling Candidate Engagement Through Questions";
    return (
      <div
        className="absolute left-1/2 transform -translate-x-1/2"
        style={{ top: "4%" }}
      >
        <h2
          className="text-lg md:text-xl font-normal text-center"
          style={{
            color: colors.textHeading,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {title}
        </h2>
      </div>
    );
  };

  // Render quadrant text
  const renderQuadrantText = (index: number) => {
    if (index >= content.steps.length) return null;

    const step = content.steps[index];
    const stepColor = getStepColor(index);
    const isEditingTitle = editingIndex === index && editingField === "title";
    const isEditingDescription =
      editingIndex === index && editingField === "description";

    let titlePos, descPos;
    switch (index % 6) {
      case 0: // Valuable Information
        titlePos = { left: "50%", top: "12%", transform: "translateX(10%)" };
        descPos = { left: "50%", top: "14%", transform: "translateX(10%)" };
        break;
      case 1: // Interest Demonstration
        titlePos = { left: "70%", top: "28%", transform: "translateX(10%)" };
        descPos = { left: "70%", top: "32%", transform: "translateX(10%)" };
        break;
      case 2: // Engagement Display
        titlePos = { left: "75%", top: "43%", transform: "translateX(10%)" };
        descPos = { left: "75%", top: "46%", transform: "translateX(10%)" };
        break;
      case 3: // Company Culture Insights
        titlePos = { left: "75%", top: "58%", transform: "translateX(10%)" };
        descPos = { left: "75%", top: "62%", transform: "translateX(10%)" };
        break;
      case 4: // Team Dynamics Exploration
        titlePos = { left: "70%", top: "73%", transform: "translateX(10%)" };
        descPos = { left: "70%", top: "77%", transform: "translateX(10%)" };
        break;
      case 5: // Growth Opportunities Discovery
        titlePos = { left: "50%", top: "89%", transform: "translateX(10%)" };
        descPos = { left: "50%", top: "92%", transform: "translateX(10%)" };
        break;
      default:
        titlePos = { left: "0", top: "0" };
        descPos = { left: "0", top: "0" };
    }

    const titleText = getQuadrantTitle(index, step);

    return (
      <>
        {/* Title */}
        <div
          className="absolute"
          style={{
            left: titlePos.left,
            top: titlePos.top,
            transform: titlePos.transform,
            zIndex: 10,
            maxWidth: "200px",
          }}
        >
          {isEditingTitle ? (
            <input
              value={tempValue}
              autoFocus
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={() => {
                setEditingIndex(null);
                setEditingField(null);
                if (tempValue !== titleText) {
                  handleContentUpdate(index, "title", tempValue);
                }
              }}
              className="text-base md:text-lg font-bold bg-transparent border border-gray-300 px-2 py-1 w-full"
              style={{ color: stepColor, fontFamily: "Roboto, sans-serif" }}
            />
          ) : (
            <h3
              className="text-base md:text-lg font-bold cursor-pointer"
              style={{ color: stepColor, fontFamily: "Roboto, sans-serif" }}
              onClick={() => {
                setEditingIndex(index);
                setEditingField("title");
                setTempValue(titleText);
              }}
            >
              {titleText}
            </h3>
          )}
        </div>

        {/* Description */}
        <div
          className="absolute"
          style={{
            left: descPos.left,
            top: descPos.top,
            transform: descPos.transform,
            zIndex: 10,
            maxWidth: "200px",
          }}
        >
          {isEditingDescription ? (
            <textarea
              value={tempValue}
              autoFocus
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={() => {
                setEditingIndex(null);
                setEditingField(null);
                if (tempValue !== step.description) {
                  handleContentUpdate(index, "description", tempValue);
                }
              }}
              className="text-xs md:text-sm bg-transparent border border-gray-300 px-2 py-1 w-full"
              style={{
                color: colors.textDescription,
                fontFamily: "Roboto, sans-serif",
              }}
              onFocus={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
            />
          ) : (
            <p
              className="text-xs md:text-sm cursor-pointer leading-relaxed"
              style={{
                color: colors.textDescription,
                fontFamily: "Roboto, sans-serif",
              }}
              onClick={() => {
                setEditingIndex(index);
                setEditingField("description");
                setTempValue(step.description || "");
              }}
            >
              {step.description}
            </p>
          )}
        </div>
      </>
    );
  };

  // Render icon
  const renderIcon = (index: number) => {
    if (index >= content.steps.length) return null;

    // SVG number positions from the provided SVG
    const iconPositions = [
      { x: 200, y: 266 }, // Number 1
      { x: 320, y: 332 }, // Number 2
      { x: 386, y: 452 }, // Number 3
      { x: 386, y: 596 }, // Number 4
      { x: 320, y: 716 }, // Number 5
      { x: 200, y: 783 }, // Number 6
    ];

    const pos = iconPositions[index % 6];
    if (!pos) return null;

    // Convert SVG coordinates to percentages
    const viewBoxWidth = 864;
    const viewBoxHeight = 1104;
    const left = (pos.x / viewBoxWidth) * 100;
    const top = (pos.y / viewBoxHeight) * 100;

    // Adjust for icon centering (icons are 32x32px, approximate SVG text size)
    // SVG text has tspan offset (x="13.39", y="45.5"), but we center the icon
    return (
      <div
        className="absolute"
        style={{
          left: `${left}%`,
          top: `${top}%`,
          transform: "translate(-50%, -50%)", // Center the icon
          zIndex: 10,
        }}
      >
        {getStepIcon(index)}
      </div>
    );
  };

  // Render quadrant shapes
  const renderQuadrantShapes = () => {
    return (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 864 1104"
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          fillRule: "evenodd",
          clipRule: "evenodd",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeMiterlimit: 1.5,
        }}
      >
        {/* Central Circle */}
        <g transform="translate(-51, 344.6640625)">
          <path
            d="M 404.6675 207.333801C 404.6675 316.318201 316.3182 404.667501 207.3338 404.667501C 98.3493 404.667501 10 316.318201 10 207.333801C 10 98.349301 98.3493 10 207.3338 10C 316.3182 10 404.6675 98.349301 404.6675 207.333801Z"
            stroke="#d3d3d3"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 404.6675 207.333801C 404.6675 316.318201 316.3182 404.667501 207.3338 404.667501C 98.3493 404.667501 10 316.318201 10 207.333801C 10 98.349301 98.3493 10 207.3338 10C 316.3182 10 404.6675 98.349301 404.6675 207.333801Z"
            stroke="#1010101a"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Step 1: Valuable Information */}
        <g transform="translate(146, 122)">
          <path
            d="M 177.5952 138.717C 128.2656 110.2731 71.0332 94 10 94L 10.3338 233C 45.9422 233 79.3477 242.4315 108.1886 258.9326L 177.5952 138.717ZM 250 22C 250 28.6274 244.6274 34 238 34C 231.3726 34 226 28.6274 226 22C 226 15.3726 231.3726 10 238 10C 244.6274 10 250 15.3726 250 22ZM 226 22L 125 22C 112.8497 22 103 31.8497 103 44L 103 107"
            stroke={colors.blue}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 177.5952 138.717C 128.2656 110.2731 71.0332 94 10 94L 10.3338 233C 45.9422 233 79.3477 242.4315 108.1886 258.9326L 177.5952 138.717ZM 250 22C 250 28.6274 244.6274 34 238 34C 231.3726 34 226 28.6274 226 22C 226 15.3726 231.3726 10 238 10C 244.6274 10 250 15.3726 250 22ZM 226 22L 125 22C 112.8497 22 103 31.8497 103 44L 103 107"
            stroke="#00ade7d7"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Step 2: Interest Demonstration */}
        <g transform="translate(244.1885986328125, 250.71875)">
          <path
            d="M 202.5936 132.8223L 82.5888 202.1071C 65.1221 172.2539 40.0365 147.4008 10 130.2156L 79.4065 10C 130.4421 39.4277 173.0186 81.8827 202.5936 132.8223ZM 307.8114 61.2813C 307.8114 67.9087 302.4388 73.2813 295.8114 73.2813C 289.184 73.2813 283.8114 67.9087 283.8114 61.2813C 283.8114 54.6538 289.184 49.2813 295.8114 49.2813C 302.4388 49.2813 307.8114 54.6538 307.8114 61.2813ZM 283.8114 61.2813L 146.9608 61.2813"
            stroke={colors.green}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 202.5936 132.8223L 82.5888 202.1071C 65.1221 172.2539 40.0365 147.4008 10 130.2156L 79.4065 10C 130.4421 39.4277 173.0186 81.8827 202.5936 132.8223ZM 307.8114 61.2813C 307.8114 67.9087 302.4388 73.2813 295.8114 73.2813C 289.184 73.2813 283.8114 67.9087 283.8114 61.2813C 283.8114 54.6538 289.184 49.2813 295.8114 49.2813C 302.4388 49.2813 307.8114 54.6538 307.8114 61.2813ZM 283.8114 61.2813L 146.9608 61.2813"
            stroke="#00c5676e"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Step 3: Engagement Display */}
        <g transform="translate(316.7770080566406, 373.53900146484375)">
          <path
            d="M 36.8901 178.7944C 36.8901 142.5029 27.0933 108.4996 10 79.2847L 130.0048 10C 158.7584 59.5251 175.2225 117.0702 175.2225 178.4607L 36.8901 178.7944ZM 283.2225 94.4609C 283.2225 101.0884 277.85 106.4609 271.2225 106.4609C 264.5951 106.4609 259.2225 101.0884 259.2225 94.4609C 259.2225 87.8335 264.5951 82.4609 271.2225 82.4609C 277.85 82.4609 283.2225 87.8335 283.2225 94.4609ZM 259.2225 94.4609L 164.7225 94.4609"
            stroke={colors.lightGreen}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 36.8901 178.7944C 36.8901 142.5029 27.0933 108.4996 10 79.2847L 130.0048 10C 158.7584 59.5251 175.2225 117.0702 175.2225 178.4607L 36.8901 178.7944ZM 283.2225 94.4609C 283.2225 101.0884 277.85 106.4609 271.2225 106.4609C 264.5951 106.4609 259.2225 101.0884 259.2225 94.4609C 259.2225 87.8335 264.5951 82.4609 271.2225 82.4609C 277.85 82.4609 283.2225 87.8335 283.2225 94.4609ZM 259.2225 94.4609L 164.7225 94.4609"
            stroke="#6b9d0056"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Step 4: Company Culture Insights */}
        <g transform="translate(317.5690002441406, 542)">
          <path
            d="M 10 108.4782C 26.6039 79.5714 36.0986 46.0614 36.0986 10.3338L 174.4311 10C 174.4311 71.0332 158.158 128.2657 129.7141 177.5952L 10 108.4782ZM 282.4311 94C 282.4311 100.6274 277.0585 106 270.4311 106C 263.8037 106 258.4311 100.6274 258.4311 94C 258.4311 87.3726 263.8037 82 270.4311 82C 277.0585 82 282.4311 87.3726 282.4311 94ZM 258.4311 94L 163.9311 94"
            stroke={colors.yellow}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 10 108.4782C 26.6039 79.5714 36.0986 46.0614 36.0986 10.3338L 174.4311 10C 174.4311 71.0332 158.158 128.2657 129.7141 177.5952L 10 108.4782ZM 282.4311 94C 282.4311 100.6274 277.0585 106 270.4311 106C 263.8037 106 258.4311 100.6274 258.4311 94C 258.4311 87.3726 263.8037 82 270.4311 82C 277.0585 82 282.4311 87.3726 282.4311 94ZM 258.4311 94L 163.9311 94"
            stroke="#e9d100ff"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Step 5: Team Dynamics Exploration */}
        <g transform="translate(245.34423828125, 640.4765625)">
          <path
            d="M 79.1166 202.303901L 10 82.590401C 39.9792 65.1676 64.9496 40.075301 82.2247 10.000001L 201.9387 79.117001C 172.5111 130.152401 130.0562 172.728901 79.1166 202.303901ZM 306.6558 151.523401C 306.6558 144.896001 301.2832 139.523401 294.6558 139.523401C 288.0283 139.523401 282.6558 144.896001 282.6558 151.523401C 282.6558 158.150901 288.0283 163.523401 294.6558 163.523401C 301.2832 163.523401 306.6558 158.150901 306.6558 151.523401ZM 282.6558 151.523401L 145.8052 151.523401"
            stroke={colors.orange}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 79.1166 202.303901L 10 82.590401C 39.9792 65.1676 64.9496 40.075301 82.2247 10.000001L 201.9387 79.117001C 172.5111 130.152401 130.0562 172.728901 79.1166 202.303901ZM 306.6558 151.523401C 306.6558 144.896001 301.2832 139.523401 294.6558 139.523401C 288.0283 139.523401 282.6558 144.896001 282.6558 151.523401C 282.6558 158.150901 288.0283 163.523401 294.6558 163.523401C 301.2832 163.523401 306.6558 158.150901 306.6558 151.523401ZM 282.6558 151.523401L 145.8052 151.523401"
            stroke="#f57600ab"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Step 6: Growth Opportunities Discovery */}
        <g transform="translate(146, 713.06640625)">
          <path
            d="M 10.3338 36.5989C 46.4188 36.5989 80.2415 26.9133 109.3442 10L 178.4609 129.7135C 128.9357 158.4673 71.3906 174.9314 10 174.9314L 10.3338 36.5989ZM 250 246.9336C 250 240.3062 244.6274 234.9336 238 234.9336C 231.3726 234.9336 226 240.3062 226 246.9336C 226 253.561 231.3726 258.9336 238 258.9336C 244.6274 258.9336 250 253.561 250 246.9336ZM 226 246.9336L 125 246.9336C 112.8497 246.9336 103 237.0839 103 224.9336L 103 161.9336"
            stroke={colors.red}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 10.3338 36.5989C 46.4188 36.5989 80.2415 26.9133 109.3442 10L 178.4609 129.7135C 128.9357 158.4673 71.3906 174.9314 10 174.9314L 10.3338 36.5989ZM 250 246.9336C 250 240.3062 244.6274 234.9336 238 234.9336C 231.3726 234.9336 226 240.3062 226 246.9336C 226 253.561 231.3726 258.9336 238 258.9336C 244.6274 258.9336 250 253.561 250 246.9336ZM 226 246.9336L 125 246.9336C 112.8497 246.9336 103 237.0839 103 224.9336L 103 161.9336"
            stroke="#f9080063"
            strokeWidth="2"
            fill="none"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
          </path>
        </g>

        {/* Central Text: Candidate Questions */}
        <g transform="translate(50, 494)">
          <text
            style={{ font: "bold 20px Roboto, sans-serif", whiteSpace: "pre" }}
            fontWeight="bold"
            fontSize="20px"
            fontFamily="Roboto, sans-serif"
            fill={colors.textDescription}
            x="12.5"
            y="33.5"
            dominantBaseline="ideographic"
          >
            Candidate Questions
          </text>
        </g>
        <g transform="translate(62, 530)">
          <text
            style={{ font: "15px Roboto, sans-serif", whiteSpace: "pre" }}
            fontSize="15px"
            fontFamily="Roboto, sans-serif"
            fill={colors.textDescription}
            x="36.7"
            y="30.5"
            dominantBaseline="ideographic"
          >
            <tspan x="36.7" y="30.5">
              Questions reveal{" "}
            </tspan>
            <tspan x="13.02" y="48.5">
              candidate's interest and{" "}
            </tspan>
            <tspan x="48.41" y="66.5">
              engagement.
            </tspan>
          </text>
        </g>
      </svg>
    );
  };

  return (
    <>
      {renderTitle()}

      <div
        ref={containerRef}
        className="relative overflow-hidden"
        style={{
          maxWidth: "700px",
          margin: "0",
          aspectRatio: `${aspectRatio}`,
        }}
      >
        {renderQuadrantShapes()}
        {content.steps.slice(0, 6).map((_, index) => renderIcon(index))}
        {content.steps.slice(0, 6).map((_, index) => renderQuadrantText(index))}
      </div>
    </>
  );
}
